import { useEffect, useRef, useState, type FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { mapearError } from "@/lib/errores";
import { Boton, Campo, Cargando, ErrorBloque, Input } from "@/components/ui";

type Estado =
  | { modo: "cargando" }
  | { modo: "alta"; factorId: string; qr: string; secreto: string }
  | { modo: "codigo"; factorId: string }
  | { modo: "error"; mensaje: string };

/**
 * Verificación en dos pasos (TOTP: Google Authenticator, Microsoft Authenticator, 1Password…). Obligatoria para admins
 * (revisión de Diego, 27-oct): sin ella el servidor responde FALTA_2FA y la base no reconoce el rol de admin.
 * Primera vez: alta con QR. Después: los 6 dígitos de la app. Al verificar, Supabase sube la sesión a aal2 y avisa con MFA_CHALLENGE_VERIFIED.
 */
export function SegundoFactor({ salir }: { salir: () => Promise<void> }) {
  const [estado, setEstado] = useState<Estado>({ modo: "cargando" });
  const [codigo, setCodigo] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const iniciado = useRef(false); // StrictMode corre el efecto dos veces; dos altas a la vez chocan

  useEffect(() => {
    if (iniciado.current) return;
    iniciado.current = true;
    void (async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) { setEstado({ modo: "error", mensaje: mapearError(error).mensaje }); return; }
      const verificado = data.totp[0];
      if (verificado) { setEstado({ modo: "codigo", factorId: verificado.id }); return; }
      // Un alta a medias (cerró antes de escribir el código) estorba a la nueva: se quita y se empieza de cero.
      for (const f of data.all.filter((x) => x.status === "unverified")) await supabase.auth.mfa.unenroll({ factorId: f.id });
      const r = await supabase.auth.mfa.enroll({ factorType: "totp", issuer: "Wealth Nation", friendlyName: "Wealth Nation" });
      if (r.error) { setEstado({ modo: "error", mensaje: mapearError(r.error).mensaje }); return; }
      setEstado({ modo: "alta", factorId: r.data.id, qr: r.data.totp.qr_code, secreto: r.data.totp.secret });
    })();
  }, []);

  async function verificar(e: FormEvent) {
    e.preventDefault();
    if (estado.modo !== "alta" && estado.modo !== "codigo") return;
    setError(null); setCargando(true);
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: estado.factorId, code: codigo.replace(/\D/g, "") });
    setCargando(false);
    if (error) { setError(mapearError(error).mensaje); setCodigo(""); }
  }

  return (
    <div className="marca-oscura min-h-full text-white">
      <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-8 px-6 py-10">
        <div className="flex flex-col gap-3 text-center">
          <p className="eyebrow text-acero">Wealth Nation · Administrador</p>
          <h1 className="text-3xl leading-tight text-[#e8eef3]">Verificación en dos pasos</h1>
          <p className="text-white/65">Como administrador puedes ver la lista de miembros y dar códigos de acceso, así que tu cuenta lleva un segundo candado además del código por correo.</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-white p-5 text-tinta shadow-2xl">
          {estado.modo === "cargando" ? <Cargando texto="Preparando…" /> : estado.modo === "error" ? <ErrorBloque mensaje={estado.mensaje} reintentar={() => window.location.reload()} /> : (
            <form onSubmit={verificar} className="flex flex-col gap-4">
              {estado.modo === "alta" && (
                <div className="flex flex-col items-center gap-3 text-center text-sm">
                  <p>Escanea este código con tu app de autenticación (Google Authenticator, Microsoft Authenticator o 1Password).</p>
                  <img src={estado.qr} alt="Código QR para la app de autenticación" width={184} height={184} className="h-46 w-46" />
                  <p className="text-tinta-suave">¿No puedes escanearlo? Escribe esta clave en la app:</p>
                  <p className="select-all break-all rounded-md bg-fondo px-3 py-2 font-mono text-xs">{estado.secreto}</p>
                </div>
              )}
              <Campo etiqueta={estado.modo === "alta" ? "Los 6 dígitos que te muestra la app" : "Código de tu app de autenticación"} htmlFor="totp" error={error ?? undefined}>
                <Input id="totp" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} required autoFocus value={codigo}
                  onChange={(e) => setCodigo(e.target.value)} className="tabular text-center font-mono text-2xl tracking-[.4em]" placeholder="······" />
              </Campo>
              <Boton type="submit" tamano="lg" cargando={cargando} disabled={codigo.replace(/\D/g, "").length !== 6}>{estado.modo === "alta" ? "Activar y entrar" : "Entrar"}</Boton>
            </form>
          )}
        </div>
        <div className="flex flex-col items-center gap-3 text-center text-sm text-white/55">
          {estado.modo === "codigo" && <p>¿Cambiaste de teléfono o borraste la app? Pide a soporte técnico que reinicie tu verificación.</p>}
          <Boton variante="fantasma" className="text-white" onClick={() => void salir()}>Salir</Boton>
        </div>
      </main>
    </div>
  );
}
